import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useCompanyCore } from '../../../../core/modules/company';
import { aiChatService, AiChatConfig, AiChatConfigUpdate, LlmProvider } from '../../../../core/modules/aiChat/services/api';
import { CompanyRole } from '../../../../core/modules/user/interfaces/role';

// Roles seleccionables en el desplegable. Tomador de Inventario queda
// siempre afuera -- ese rol nunca tiene acceso al chatbot sin importar esta
// config (su UI es un arbol aparte, CountingLayout, sin este widget) -- ver
// _require_chat_access en el backend (routers/ai_chat_router.py).
const SELECTABLE_ROLES = [CompanyRole.USER, CompanyRole.COMPANY_ADMIN];

const ROLE_LABELS: Record<string, string> = {
  [CompanyRole.USER]: 'Usuario',
  [CompanyRole.COMPANY_ADMIN]: 'Administrador de empresa',
};

// Datos por proveedor -- un solo lugar para agregar un cuarto proveedor
// algun dia sin tocar el resto del componente.
const PROVIDER_LABELS: Record<LlmProvider, string> = {
  [LlmProvider.ANTHROPIC]: 'Anthropic (Claude)',
  [LlmProvider.OPENAI]: 'OpenAI (ChatGPT)',
  [LlmProvider.GEMINI]: 'Google Gemini',
};

const PROVIDER_KEY_PLACEHOLDERS: Record<LlmProvider, string> = {
  [LlmProvider.ANTHROPIC]: 'sk-ant-...',
  [LlmProvider.OPENAI]: 'sk-...',
  [LlmProvider.GEMINI]: 'AIza...',
};

const hasKeyForProvider = (config: AiChatConfig | null, provider: LlmProvider): boolean => {
  if (!config) return false;
  if (provider === LlmProvider.OPENAI) return config.hasOpenaiApiKey;
  if (provider === LlmProvider.GEMINI) return config.hasGeminiApiKey;
  return config.hasAnthropicApiKey;
};

// Arma el patch de una sola key sin usar claves computadas -- mas facil de
// leer y evita pelear con el tipado de AiChatConfigUpdate.
const buildApiKeyUpdate = (provider: LlmProvider, value: string | null): AiChatConfigUpdate => {
  if (provider === LlmProvider.OPENAI) return { openaiApiKey: value };
  if (provider === LlmProvider.GEMINI) return { geminiApiKey: value };
  return { anthropicApiKey: value };
};

const AiChatTab = () => {
  const { company } = useCompanyCore();
  const companyId = company?.id ? Number(company.id) : null;

  const [config, setConfig] = useState<AiChatConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Proveedor elegido en el desplegable -- no se guarda solo, hace falta
  // "Guardar proveedor". El campo de API key de mas abajo siempre apunta a
  // ESTE proveedor (no necesariamente el ya guardado), para poder cargar la
  // key de un proveedor antes de cambiarse a el.
  const [selectedProvider, setSelectedProvider] = useState<LlmProvider>(LlmProvider.ANTHROPIC);
  const [savingProvider, setSavingProvider] = useState(false);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);

  const [selectedRoles, setSelectedRoles] = useState<CompanyRole[]>([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const [savingEnabled, setSavingEnabled] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await aiChatService.getConfig(companyId);
      setConfig(data);
      setSelectedRoles(data.allowedRoles);
      setSelectedProvider(data.llmProvider);
    } catch (err) {
      setError('No se pudo cargar la configuración del chatbot de datos.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleProviderChange = (event: SelectChangeEvent<LlmProvider>) => {
    setSelectedProvider(event.target.value as LlmProvider);
    // La key mostrada es siempre la del proveedor seleccionado -- al
    // cambiar de proveedor se limpia el campo para no dejar a medio
    // escribir una key que en realidad es de otro proveedor.
    setApiKeyInput('');
  };

  const providerIsPristine = !!config && config.llmProvider === selectedProvider;

  const handleSaveProvider = async () => {
    if (!companyId) return;
    setSavingProvider(true);
    setError(null);
    setInfo(null);
    try {
      const result = await aiChatService.updateConfig(companyId, { llmProvider: selectedProvider });
      setConfig(result);
      setSelectedRoles(result.allowedRoles);
      setSelectedProvider(result.llmProvider);
      setInfo(`El chatbot ahora usa ${PROVIDER_LABELS[result.llmProvider]}.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo cambiar el proveedor de IA.');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleSaveApiKey = async () => {
    if (!companyId) return;
    const trimmed = apiKeyInput.trim();
    if (!trimmed) return;
    setSavingApiKey(true);
    setError(null);
    setInfo(null);
    try {
      const result = await aiChatService.updateConfig(companyId, buildApiKeyUpdate(selectedProvider, trimmed));
      setConfig(result);
      setSelectedRoles(result.allowedRoles);
      setApiKeyInput('');
      setInfo(`API key de ${PROVIDER_LABELS[selectedProvider]} guardada.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo guardar la API key. Verifica que sea correcta.');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleRemoveApiKey = async () => {
    if (!companyId) return;
    setSavingApiKey(true);
    setError(null);
    setInfo(null);
    try {
      const result = await aiChatService.updateConfig(companyId, buildApiKeyUpdate(selectedProvider, null));
      setConfig(result);
      setSelectedRoles(result.allowedRoles);
      setApiKeyInput('');
      setInfo(
        `Se quitó la API key de ${PROVIDER_LABELS[selectedProvider]}` +
          (result.llmProvider === selectedProvider
            ? ': el chatbot queda desactivado para esta empresa hasta que se configure una nueva.'
            : '.')
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo quitar la API key.');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleRolesChange = (event: SelectChangeEvent<CompanyRole[]>) => {
    const value = event.target.value;
    setSelectedRoles(typeof value === 'string' ? (value.split(',') as CompanyRole[]) : value);
  };

  const rolesArePristine =
    !!config &&
    selectedRoles.length === config.allowedRoles.length &&
    selectedRoles.every((r) => config.allowedRoles.includes(r));

  const handleSaveRoles = async () => {
    if (!companyId) return;
    setSavingRoles(true);
    setError(null);
    setInfo(null);
    try {
      const result = await aiChatService.updateConfig(companyId, { allowedRoles: selectedRoles });
      setConfig(result);
      setSelectedRoles(result.allowedRoles);
      setInfo('Roles habilitados actualizados.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudieron guardar los roles habilitados.');
    } finally {
      setSavingRoles(false);
    }
  };

  // Interruptor general del modulo (Company.ai_chat_enabled). Esta pestaña
  // solo la ve un superadmin (Company.tsx redirige a cualquier otro rol),
  // asi que no hace falta un chequeo de permisos adicional en el front --
  // el backend igual lo vuelve a exigir por su cuenta.
  const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyId) return;
    const nextValue = event.target.checked;
    setSavingEnabled(true);
    setError(null);
    setInfo(null);
    try {
      const result = await aiChatService.updateConfig(companyId, { aiChatEnabled: nextValue });
      setConfig(result);
      setSelectedRoles(result.allowedRoles);
      setInfo(nextValue ? 'Chatbot habilitado para esta empresa.' : 'Chatbot deshabilitado para esta empresa.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo cambiar el estado del chatbot.');
    } finally {
      setSavingEnabled(false);
    }
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center p-8">
        <Card className="w-full max-w-2xl">
          <CardContent>
            <Skeleton variant="rectangular" height={340} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="flex items-center justify-center p-4 sm:p-8">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardContent className="p-6 sm:p-8">
          <Stack spacing={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesomeIcon color="action" fontSize="small" />
              <Typography variant="h5" component="h2" className="font-bold">
                Chatbot de datos (IA)
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              El widget de chat que le permite al cliente preguntarle a sus datos usa tu propia API
              key (BYOK) del proveedor de IA que elijas -- Inventaria no paga el costo de IA de esta
              empresa. Sin una key configurada para el proveedor activo, el chatbot le muestra un
              mensaje pidiendo que se configure.
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!config?.aiChatEnabled}
                    onChange={handleToggleEnabled}
                    disabled={savingEnabled}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600} color={config?.aiChatEnabled ? 'success.main' : 'text.secondary'}>
                    {config?.aiChatEnabled ? 'Chatbot habilitado para esta empresa' : 'Chatbot deshabilitado para esta empresa'}
                  </Typography>
                }
              />
              {savingEnabled && <CircularProgress size={16} />}
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" onClose={() => setInfo(null)}>
                {info}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Proveedor de IA
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Elige que proveedor usa el chatbot para esta empresa. Cambiar de proveedor no borra
                las API keys de los demás -- quedan guardadas por si se vuelve a cambiar.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth>
                    <InputLabel id="ai-chat-provider-label">Proveedor</InputLabel>
                    <Select
                      labelId="ai-chat-provider-label"
                      label="Proveedor"
                      value={selectedProvider}
                      onChange={handleProviderChange}
                    >
                      {Object.values(LlmProvider).map((provider) => (
                        <MenuItem key={provider} value={provider}>
                          {PROVIDER_LABELS[provider]}
                          {hasKeyForProvider(config, provider) ? '' : ' (sin API key configurada)'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={savingProvider || providerIsPristine}
                    onClick={handleSaveProvider}
                  >
                    {savingProvider ? <CircularProgress size={20} /> : 'Guardar proveedor'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <TextField
                  label={`API key de ${PROVIDER_LABELS[selectedProvider]}`}
                  placeholder={PROVIDER_KEY_PLACEHOLDERS[selectedProvider]}
                  type="password"
                  fullWidth
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  helperText={
                    hasKeyForProvider(config, selectedProvider)
                      ? 'Ya hay una API key configurada para este proveedor (no se muestra por seguridad). Escribe una nueva para reemplazarla.'
                      : 'Todavía no hay una API key configurada para este proveedor.'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={savingApiKey || apiKeyInput.trim() === ''}
                    onClick={handleSaveApiKey}
                  >
                    {savingApiKey ? <CircularProgress size={20} /> : 'Guardar'}
                  </Button>
                  {hasKeyForProvider(config, selectedProvider) && (
                    <Button variant="outlined" color="error" fullWidth disabled={savingApiKey} onClick={handleRemoveApiKey}>
                      Quitar
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>

            {selectedProvider === LlmProvider.ANTHROPIC && (
              <Typography variant="caption" color="text.secondary">
                Al crear la key en la consola de Anthropic, asígnala a un workspace específico (no la
                dejes sin workspace) para evitar errores de autenticación.
              </Typography>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Roles con acceso al chatbot
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Elige qué roles de esta empresa pueden usar el chatbot (además de que esté habilitado
                y tenga una API key configurada). El rol Tomador de Inventario nunca tiene acceso,
                sin importar esta selección -- su pantalla es otra, sin este widget.
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth>
                    <InputLabel id="ai-chat-roles-label">Roles habilitados</InputLabel>
                    <Select
                      labelId="ai-chat-roles-label"
                      label="Roles habilitados"
                      multiple
                      value={selectedRoles}
                      onChange={handleRolesChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              Ningún rol habilitado
                            </Typography>
                          ) : (
                            selected.map((role) => <Chip key={role} label={ROLE_LABELS[role] ?? role} size="small" />)
                          )}
                        </Box>
                      )}
                    >
                      {SELECTABLE_ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                          {ROLE_LABELS[role]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button variant="contained" fullWidth disabled={savingRoles || rolesArePristine} onClick={handleSaveRoles}>
                    {savingRoles ? <CircularProgress size={20} /> : 'Guardar roles'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AiChatTab;
